import React, { useCallback, useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { X, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { cn } from "lib/utils";
import { getNavSections } from "../../navigation";
import { useSidebar } from "../../SidebarContext";
import { SidebarLink, SidebarSectionLabel, SidebarGroup } from "./SidebarNav";
import { UserMenu } from "./UserMenu";
import merkoLogo from "../../../../../src/assets/merko-logo.svg";

/* ══════════════════════════════════════════════════════════════════════════
   Application sidebar.

   Kept: the dark navy rail, the indigo active treatment, the collapse toggle,
   the localStorage preference, swipe-to-close, safe-area padding, and the
   account menu with its plan upsell.

   Changed:
   • Destinations come from the shared navigation registry and are grouped
     under section headings instead of one 14-item list. The AI-agent links
     now point at routes that actually exist — every `/agents/*` path in the
     old list fell through to the dashboard redirect.
   • State lives in SidebarContext rather than `window` custom events, so the
     header can set `aria-expanded` and the drawer can be closed from anywhere.
   • The mobile drawer is a real dialog: labelled, Escape-closable, focus moves
     into it on open and returns to the trigger on close.
   • Typography uses the type scale rather than `text-[13px]` / `text-[10.5px]`.
   ══════════════════════════════════════════════════════════════════════════ */

const Sidebar: React.FC = () => {
    const location = useLocation();
    const { collapsed, toggleCollapsed, mobileOpen, closeMobile, isDesktop } = useSidebar();
    const asideRef = useRef<HTMLElement | null>(null);
    const touchStartX = useRef(0);
    const touchStartY = useRef(0);
    const restoreFocusTo = useRef<HTMLElement | null>(null);

    const sections = getNavSections();

    /* Close the drawer when the route changes on mobile. */
    useEffect(() => {
        if (!isDesktop) closeMobile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    /* Move focus into the drawer on open, and back to the opener on close. */
    useEffect(() => {
        if (isDesktop) return;
        if (mobileOpen) {
            restoreFocusTo.current = document.activeElement as HTMLElement;
            const first = asideRef.current?.querySelector<HTMLElement>(
                'a[href], button:not([disabled])'
            );
            first?.focus();
        } else {
            restoreFocusTo.current?.focus?.();
            restoreFocusTo.current = null;
        }
    }, [mobileOpen, isDesktop]);

    /* Swipe left to dismiss. */
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        touchStartX.current = e.touches[0].clientX;
        touchStartY.current = e.touches[0].clientY;
    }, []);

    const handleTouchEnd = useCallback(
        (e: React.TouchEvent) => {
            const dx = e.changedTouches[0].clientX - touchStartX.current;
            const dy = Math.abs(e.changedTouches[0].clientY - touchStartY.current);
            if (dx < -60 && dy < 100 && !isDesktop) closeMobile();
        },
        [isDesktop, closeMobile]
    );

    const onNavigate = () => {
        if (!isDesktop) closeMobile();
    };

    const isCollapsed = isDesktop ? collapsed : false;

    return (
        <>
            {/* ── Backdrop ── */}
            {!isDesktop && mobileOpen && (
                <div
                    className="fixed inset-0 z-[55] lg:hidden bg-black/65 backdrop-blur-[6px] animate-overlay-in"
                    onClick={closeMobile}
                    aria-hidden="true"
                />
            )}

            <aside
                ref={asideRef}
                id="app-sidebar"
                role={!isDesktop ? "dialog" : undefined}
                aria-modal={!isDesktop && mobileOpen ? true : undefined}
                aria-label="Main navigation"
                aria-hidden={!isDesktop && !mobileOpen ? true : undefined}
                className={cn(
                    /* `z-[60]` is for the mobile drawer only, where it has to sit
                       above its own backdrop at `z-[55]`. On desktop the sidebar is
                       an ordinary column, and that z-index only created a stacking
                       context high enough to bury every dialog on the page — the
                       hand-rolled overlays all sit at `z-50`. */
                    "fixed lg:relative top-0 bottom-0 left-0 z-[60] lg:z-auto flex flex-col",
                    "w-[85vw] max-w-[300px] sm:w-[280px]",
                    isCollapsed ? "lg:w-[68px]" : "lg:w-[248px]",
                    "bg-[#111827] text-white border-r border-white/[0.06]",
                    "transform transition-transform duration-300 ease-ds-out lg:transform-none",
                    isDesktop ? "translate-x-0" : mobileOpen ? "translate-x-0" : "-translate-x-full",
                    "safe-padding-top safe-padding-bottom safe-padding-left"
                )}
                style={{ height: "100dvh", minHeight: "-webkit-fill-available" }}
                onTouchStart={!isDesktop ? handleTouchStart : undefined}
                onTouchEnd={!isDesktop ? handleTouchEnd : undefined}
            >
                {/* ── Collapse toggle (desktop) ── */}
                {isDesktop && (
                    <button
                        type="button"
                        onClick={toggleCollapsed}
                        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        aria-expanded={!isCollapsed}
                        aria-controls="app-sidebar"
                        className={cn(
                            "absolute z-50 top-[42px] -right-3 w-6 h-6 rounded-full",
                            "inline-flex items-center justify-center",
                            "bg-[#1e1b4b] text-brand-400 border-[1.5px] border-brand-500/45",
                            "shadow-[0_0_10px_rgba(99,102,241,0.25),0_2px_8px_rgba(0,0,0,0.6)]",
                            "transition-transform duration-200 hover:scale-110",
                            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[#111827]"
                        )}
                    >
                        {isCollapsed ? (
                            <PanelLeftOpen className="w-3 h-3" />
                        ) : (
                            <PanelLeftClose className="w-3 h-3" />
                        )}
                    </button>
                )}

                {/* ── Brand ── */}
                <div
                    className={cn(
                        "relative flex items-center flex-shrink-0 h-14 border-b border-white/[0.08]",
                        isCollapsed ? "justify-center px-2" : "px-3"
                    )}
                    style={{
                        background:
                            "linear-gradient(160deg, rgba(99,102,241,0.12) 0%, rgba(99,102,241,0.04) 60%, transparent 100%)",
                    }}
                >
                    <NavLink
                        to="/dashboard"
                        onClick={onNavigate}
                        aria-label="Merko — go to dashboard"
                        className="flex items-center min-w-0 rounded-lg transition-opacity hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                    >
                        <img src={merkoLogo} alt="Merko" className={isCollapsed ? "w-[46px]" : "h-9"} />
                    </NavLink>

                    {!isDesktop && (
                        <button
                            type="button"
                            onClick={closeMobile}
                            aria-label="Close navigation"
                            className="ml-auto inline-flex items-center justify-center w-11 h-11 rounded-xl text-slate-400 hover:text-white bg-white/[0.04] hover:bg-white/[0.1] transition-colors touch-manipulation focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    )}
                </div>

                {/* ── Destinations ── */}
                <nav
                    aria-label="Primary"
                    className={cn(
                        "flex-1 overflow-y-auto overflow-x-hidden py-2 w-full max-w-full min-w-0",
                        "scrollbar-slim scrollbar-slim--on-dark",
                        isCollapsed ? "px-2" : "px-2.5"
                    )}
                    style={
                        {
                            overscrollBehavior: "contain",
                            WebkitOverflowScrolling: "touch",
                        } as React.CSSProperties
                    }
                >
                    {sections.map((section, index) =>
                        section.collapsible ? (
                            <SidebarGroup
                                key={section.id}
                                section={section}
                                collapsed={isCollapsed}
                                onNavigate={onNavigate}
                            />
                        ) : (
                            <div key={section.id} className="flex flex-col items-stretch">
                                {index > 0 && (
                                    <SidebarSectionLabel label={section.label} collapsed={isCollapsed} />
                                )}
                                <div className="space-y-0.5 flex flex-col items-stretch">
                                    {section.items.map((item) => (
                                        <SidebarLink
                                            key={item.id}
                                            item={item}
                                            collapsed={isCollapsed}
                                            onNavigate={onNavigate}
                                        />
                                    ))}
                                </div>
                            </div>
                        )
                    )}
                </nav>

                {/* ── Account ── */}
                <div
                    className={cn(
                        "flex-shrink-0 border-t border-white/[0.07] py-2 w-full max-w-full min-w-0",
                        isCollapsed ? "px-2" : "px-2"
                    )}
                >
                    <UserMenu collapsed={isCollapsed} onNavigate={onNavigate} />
                </div>
            </aside>
        </>
    );
};

export default Sidebar;
